import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSession } from "../lib/session";
import { getCompetition, myRounds, myStanding } from "../lib/api";
import { supabase } from "../lib/supabase";
import { PageHead, Loading, Notice, Empty } from "../components/ui";

/**
 * The season.
 *
 * Played rounds ticked, the live one opened out with the two things it
 * wants from you side by side, the rest waiting. This replaces the old
 * competition page, which existed only to hold links to other pages.
 */

const hhmm = (t) => (t ?? "").slice(0, 5);

export default function Rounds() {
  const { id } = useParams();
  const { profile, isOrganiser } = useSession();

  const [comp, setComp] = useState(null);
  const [mine, setMine] = useState([]);
  const [standing, setStanding] = useState(null);
  const [tee, setTee] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const c = await getCompetition(id);
        const ids = (c?.rounds ?? []).map((r) => r.id);
        const [r, s, tt] = await Promise.all([
          myRounds(id, profile.id).catch(() => []),
          myStanding(id, profile.id).catch(() => null),
          ids.length
            ? supabase.from("v_my_tee_time").select("*").in("round_id", ids)
                .then(({ data }) => data ?? [])
            : Promise.resolve([]),
        ]);
        setComp(c); setMine(r); setStanding(s); setTee(tt);
        setReady(true);
      } catch (e) { setError(e.message); setReady(true); }
    })();
  }, [id, profile?.id]);

  if (!ready) return <Loading />;
  if (!comp) return <Notice kind="error">That competition isn't available.</Notice>;

  const rounds = [...(comp.rounds ?? [])].sort((a, b) => a.round_number - b.round_number);
  const cardFor = (rid) => mine.find((m) => m.rounds?.id === rid);
  const teeFor = (rid) => tee.find((t) => t.round_id === rid && t.posted_at);
  const played = mine.filter((m) => ["submitted", "no_return"].includes(m.card_status)).length;

  return (
    <>
      <PageHead title={comp.name}
                sub={[rounds.length === 1 ? "One round"
                        : comp.aggregation === "best_n"
                          ? `Best ${comp.counting_rounds} of ${rounds.length}`
                          : `All ${rounds.length} rounds count`,
                      `${played} played`].join(" · ")}>
        {isOrganiser && (
          <Link className="btn btn-ghost" to={`/c/${id}/field`}
                style={{ textDecoration: "none" }}>Who's playing</Link>
        )}
      </PageHead>

      <Notice kind="error">{error}</Notice>

      {rounds.length === 0 ? (
        <Empty title="No rounds yet">
          This competition has no rounds on it.
        </Empty>
      ) : (
        <div className="stack" style={{ gap: ".55rem" }}>
          {rounds.map((r) => {
            const card = cardFor(r.id);
            const done = ["submitted", "no_return"].includes(card?.card_status);
            const live = !done && ["open", "in_play", "scoring"].includes(r.status);
            const t = teeFor(r.id);
            const when = [r.courses?.name,
                          r.played_on && new Date(r.played_on).toLocaleDateString(
                            "en-GB", { day: "numeric", month: "long" })]
                         .filter(Boolean).join(" · ");

            if (!live) {
              return (
                <Link to={`/c/${id}/r/${r.id}`} className={`rnd${done ? " done" : ""}`} key={r.id}>
                  <span className="rnd-dot" aria-hidden="true">{done ? "" : r.round_number}</span>
                  <span className="rnd-text">
                    <strong>{r.name}</strong>
                    <small>
                      {when}
                      {done && card?.card_status === "no_return" && " · no return"}
                    </small>
                  </span>
                  <span className="rnd-chev" aria-hidden="true" />
                </Link>
              );
            }

            return (
              <div className="rnd live" key={r.id}>
                <span className="rnd-dot now" aria-hidden="true">{r.round_number}</span>
                <span className="rnd-text">
                  <Link to={`/c/${id}/r/${r.id}`} className="rnd-head">
                    <strong>{r.name}</strong>
                    <small>{when}</small>
                  </Link>

                  <span className="rnd-pair">
                    {card ? (
                      <Link to={`/cards/${card.id}`} className="rnd-do">
                        <strong>Your card</strong>
                        <small>
                          {card.card_status === "in_progress" ? "part entered" : "not started"}
                        </small>
                      </Link>
                    ) : (
                      <span className="rnd-flat">
                        <strong>Your card</strong>
                        <small>you're not in this round</small>
                      </span>
                    )}

                    <Link to={`/c/${id}/r/${r.id}`} className="rnd-flat">
                      <strong>Tee times</strong>
                      <small>{t ? `you're out at ${hhmm(t.tee_at)}` : "not posted yet"}</small>
                    </Link>
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
